import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import AuthModal from '../components/AuthModal';
import JoinEventModal from '../components/JoinEventModal';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Gift, Calendar, Users, TrendingUp, LogOut, Edit, IndianRupee, PartyPopper, HandHeart, Crown, Sparkles, Download, Receipt, Shield } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [authAction, setAuthAction] = useState('');
  const [stats, setStats] = useState(null);
  const [publicStats, setPublicStats] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  
  // Check if user is admin
  const ADMIN_EMAILS = ['support@contribo.in', 'thefinalshot3@gmail.com', 'intunetesting121@gmail.com', 'mcitpashok@gmail.com', 'anushaammu1103@gmail.com'];
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);
  
  // Debug logging
  useEffect(() => {
    if (user) {
      console.log('User email:', user.email);
      console.log('Is Admin:', isAdmin);
    }
  }, [user, isAdmin]);
  
  // Rotating words animation - Telugu/Hindi on left, English on right
  const leftWords = ['दान', 'चंदा', 'సంభావన', 'చదివింపులు']; // Hindi and Telugu
  const rightWords = ['Contribution', 'Donation', 'Gifts', 'Presents']; // English
  const [currentLeftIndex, setCurrentLeftIndex] = useState(0);
  const [currentRightIndex, setCurrentRightIndex] = useState(0);
  const [leftFadeClass, setLeftFadeClass] = useState('opacity-100');
  const [rightFadeClass, setRightFadeClass] = useState('opacity-100');

  // Rotating words animation effect for left side (Telugu/Hindi)
  useEffect(() => {
    const interval = setInterval(() => {
      setLeftFadeClass('opacity-0'); // Fade out
      
      setTimeout(() => {
        setCurrentLeftIndex((prevIndex) => (prevIndex + 1) % leftWords.length);
        setLeftFadeClass('opacity-100'); // Fade in
      }, 500); // Wait 500ms before changing word
    }, 2000); // Change every 2 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Rotating words animation effect for right side (English)
  useEffect(() => {
    const interval = setInterval(() => {
      setRightFadeClass('opacity-0'); // Fade out
      
      setTimeout(() => {
        setCurrentRightIndex((prevIndex) => (prevIndex + 1) % rightWords.length);
        setRightFadeClass('opacity-100'); // Fade in
      }, 500); // Wait 500ms before changing word
    }, 2000); // Change every 2 seconds
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (user) {
      fetchDashboardStats();
    } else {
      fetchPublicStats();
    }

    // PWA Install prompt listener
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [user]);

  // Refetch stats when page becomes visible (user returns from another page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        fetchDashboardStats();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/users/dashboard-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchPublicStats = async () => {
    try {
      const response = await axios.get(`${API}/public/app-stats`);
      setPublicStats(response.data);
    } catch (error) {
      console.error('Failed to fetch public stats:', error);
    }
  };

  const handleCreateEvent = () => {
    if (user) {
      navigate('/select-plan');
    } else {
      setAuthAction('create');
      setShowAuthModal(true);
    }
  };

  const handleJoinEvent = () => {
    setShowJoinModal(true);
  };

  const handleDashboard = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      setAuthAction('dashboard');
      setShowAuthModal(true);
    }
  };

  const handleEditEvent = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      setAuthAction('edit');
      setShowAuthModal(true);
    }
  };

  const handleInstallApp = async () => {
    if (deferredPrompt) {
      // Track install button click (to GA4 and backend)
      trackEvent('pwa_install_button_clicked', {
        event_category: 'PWA',
        event_label: 'User clicked Install App button',
        user_logged_in: !!user
      });
      
      // Also log to backend
      try {
        await axios.post(`${API}/analytics/pwa-event`, {
          event_type: 'pwa_install_button_clicked',
          user_id: user?.id || null,
          platform: /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'iOS' : 'Android',
          user_logged_in: !!user,
          user_agent: navigator.userAgent
        });
      } catch (error) {
        console.error('Failed to log button click to backend:', error);
      }
      
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        trackEvent('pwa_install_accepted', {
          event_category: 'PWA',
          event_label: 'User accepted install prompt',
          user_logged_in: !!user
        });
        
        // Log to backend
        try {
          await axios.post(`${API}/analytics/pwa-event`, {
            event_type: 'pwa_install_accepted',
            user_id: user?.id || null,
            platform: /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'iOS' : 'Android',
            user_logged_in: !!user,
            user_agent: navigator.userAgent
          });
        } catch (error) {
          console.error('Failed to log acceptance to backend:', error);
        }
      } else {
        console.log('User dismissed the install prompt');
        trackEvent('pwa_install_dismissed', {
          event_category: 'PWA',
          event_label: 'User dismissed install prompt',
          user_logged_in: !!user
        });
        
        // Log to backend
        try {
          await axios.post(`${API}/analytics/pwa-event`, {
            event_type: 'pwa_install_dismissed',
            user_id: user?.id || null,
            platform: /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'iOS' : 'Android',
            user_logged_in: !!user,
            user_agent: navigator.userAgent
          });
        } catch (error) {
          console.error('Failed to log dismissal to backend:', error);
        }
      }
      
      setDeferredPrompt(null);
      setShowInstallButton(false);
    }
  };

  const handlePurchaseHistory = () => {
    navigate('/plan-purchase-history');
  };

  const handleAdminDashboard = () => {
    navigate('/admin');
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Header */}
      <header className="glass-card sticky top-0 z-50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img 
              src="/contribo-logo.png" 
              alt="Contribo Logo" 
              className="h-12 w-auto"
            />
            <h1 className="text-2xl font-bold text-contribo-gold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Contribo
            </h1>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            {user ? (
              <>
                <span className="hidden sm:inline text-sm text-gray-600">Hi, {user.name}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePurchaseHistory}
                  className="flex items-center gap-1"
                >
                  <Receipt className="h-4 w-4" />
                  <span className="hidden md:inline">Purchase History</span>
                </Button>
                {showInstallButton && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleInstallApp}
                    className="bg-contribo-navy hover:bg-contribo-gold flex items-center gap-1"
                  >
                    <Download className="h-4 w-4" />
                    <span className="hidden md:inline">Install App</span>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden md:inline">Logout</span>
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowAuthModal(true)}
                  data-testid="login-btn"
                >
                  Login
                </Button>
                {showInstallButton && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleInstallApp}
                    className="bg-contribo-navy hover:bg-contribo-gold"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Install App</span>
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {/* Coupon Banner - Scrolling */}
      <div className="bg-gradient-to-r from-green-600 via-green-500 to-green-600 py-3 overflow-hidden relative">
        <div className="coupon-scroll flex items-center whitespace-nowrap">
          <span className="text-white font-bold text-lg mx-8 animate-pulse">
            🎉 Special Offer! Use code <span className="bg-white text-green-600 px-3 py-1 rounded-full mx-2">NEWUSER100</span> for your first event FREE! 
          </span>
          <span className="text-white font-bold text-lg mx-8 animate-pulse">
            🎉 Special Offer! Use code <span className="bg-white text-green-600 px-3 py-1 rounded-full mx-2">NEWUSER100</span> for your first event FREE! 
          </span>
          <span className="text-white font-bold text-lg mx-8 animate-pulse">
            🎉 Special Offer! Use code <span className="bg-white text-green-600 px-3 py-1 rounded-full mx-2">NEWUSER100</span> for your first event FREE! 
          </span>
          <span className="text-white font-bold text-lg mx-8 animate-pulse">
            🎉 Special Offer! Use code <span className="bg-white text-green-600 px-3 py-1 rounded-full mx-2">NEWUSER100</span> for your first event FREE! 
          </span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center fade-in">
            {/* Big Logo with Animated Words */}
            <div className="mb-8 flex justify-center items-center gap-2 md:gap-4">
              {/* Left Animated Word - Telugu/Hindi */}
              <div className="w-24 md:w-36 text-right">
                <span 
                  className={`text-base md:text-2xl font-bold text-contribo-gold transition-opacity duration-500 ${leftFadeClass} block`}
                  style={{ fontFamily: "'Noto Sans Telugu', 'Noto Sans Devanagari', sans-serif" }}
                >
                  {leftWords[currentLeftIndex]}
                </span>
              </div>
              
              {/* Contribo Logo */}
              <img 
                src="/contribo-logo.png" 
                alt="Contribo" 
                className="h-24 sm:h-40 md:h-48 lg:h-56 w-auto flex-shrink-0"
              />
              
              {/* Right Animated Word - English */}
              <div className="w-24 md:w-36 text-left">
                <span 
                  className={`text-base md:text-2xl font-bold text-contribo-gold transition-opacity duration-500 ${rightFadeClass} block`}
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {rightWords[currentRightIndex]}
                </span>
              </div>
            </div>

            <p className="text-xl sm:text-3xl text-contribo-gold-dark max-w-3xl mx-auto mb-8 font-bold">
              RSVP → Blessing → Contribution → Celebration
            </p>

            {/* NO COMMISSION Message */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 text-center max-w-3xl mx-auto mb-10">
              <p className="text-sm text-blue-800 space-y-1">
                <span className="block font-bold">🎉🥳 Creators receive directly, contributors give wholeheartedly 🎉🥳</span>
                <span className="block font-bold">✨🎊 Simple, transparent pricing—empowering every event creator ✨🎊</span>
              </p>
            </div>

            <div className="text-base sm:text-lg max-w-4xl mx-auto mb-10 leading-relaxed space-y-2">
              <p className="text-contribo-gold font-semibold">
                No more guessing gifts. Let guests contribute directly to what you truly need—transparent, easy, and purposeful.
              </p>
              <p className="text-contribo-gold font-semibold">
                From bachelor parties to cradle ceremonies, birthdays to corporate lunches—Contribo makes every event meaningful.
              </p>
              <p className="text-contribo-gold font-semibold">
                Be it venue rental, photography, food, or any other need—you set the goal, guests contribute.
              </p>
              <p className="text-contribo-navy font-semibold">
                Just RSVP, bless, and give with confidence. Every rupee is meaningfully utilized.
              </p>
            </div>

            {/* Action Tiles - 2x2 Grid */}
            {user ? (
              <>
                <div className="grid grid-cols-2 gap-4 max-w-3xl mx-auto mt-8">
                  {/* Host/Co-Host Tile */}
                  <Card 
                    className="glass-card hover:shadow-2xl transition-all cursor-pointer border-2 border-contribo-navy hover:border-contribo-gold"
                    onClick={handleCreateEvent}
                    data-testid="create-event-btn"
                  >
                    <CardContent className="pt-6 px-4 pb-6">
                      <div className="text-center">
                        <div className="bg-gradient-to-br from-contribo-navy to-contribo-gold p-4 rounded-full w-fit mx-auto mb-3">
                          <Calendar className="h-8 w-8 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-contribo-navy mb-1">I'm a Host or Co-host</h3>
                        <p className="text-xs text-gray-600">Create new event</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Guest Tile */}
                  <Card 
                    className="glass-card hover:shadow-2xl transition-all cursor-pointer border-2 border-purple-600 hover:border-purple-700"
                    onClick={handleJoinEvent}
                    data-testid="join-event-btn"
                  >
                    <CardContent className="pt-6 px-4 pb-6">
                      <div className="text-center">
                        <div className="bg-purple-600 p-4 rounded-full w-fit mx-auto mb-3">
                          <Users className="h-8 w-8 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-purple-600 mb-1">I'm a Guest</h3>
                        <p className="text-xs text-gray-600">Join an event</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Event Dashboard Tile */}
                  <Card 
                    className="glass-card hover:shadow-2xl transition-all cursor-pointer border-2 border-green-600 hover:border-green-700"
                    onClick={handleDashboard}
                    data-testid="dashboard-btn"
                  >
                    <CardContent className="pt-6 px-4 pb-6">
                      <div className="text-center">
                        <div className="bg-green-600 p-4 rounded-full w-fit mx-auto mb-3">
                          <TrendingUp className="h-8 w-8 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-green-600 mb-1">Event Dashboard</h3>
                        <p className="text-xs text-gray-600">Manage your events</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Admin Tile - Only for Admins */}
                  {isAdmin ? (
                    <Card 
                      className="glass-card hover:shadow-2xl transition-all cursor-pointer border-2 border-red-600 hover:border-red-700"
                      onClick={handleAdminDashboard}
                      data-testid="admin-btn"
                    >
                      <CardContent className="pt-6 px-4 pb-6">
                        <div className="text-center">
                          <div className="bg-gradient-to-br from-red-600 to-red-700 p-4 rounded-full w-fit mx-auto mb-3">
                            <Shield className="h-8 w-8 text-white" />
                          </div>
                          <h3 className="text-lg font-bold text-red-600 mb-1">Admin</h3>
                          <p className="text-xs text-gray-600">Admin dashboard</p>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card 
                      className="glass-card hover:shadow-2xl transition-all cursor-pointer border-2 border-contribo-gold hover:border-contribo-navy"
                      onClick={handleEditEvent}
                      data-testid="edit-event-btn"
                    >
                      <CardContent className="pt-6 px-4 pb-6">
                        <div className="text-center">
                          <div className="bg-contribo-gold p-4 rounded-full w-fit mx-auto mb-3">
                            <Edit className="h-8 w-8 text-white" />
                          </div>
                          <h3 className="text-lg font-bold text-contribo-gold mb-1">Edit Event</h3>
                          <p className="text-xs text-gray-600">Update event details</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
                
                {/* Dashboard Stats Tiles - 2x2 Grid */}
                {stats && (
                  <div className="mt-12 grid grid-cols-2 gap-4 max-w-2xl mx-auto">
                    {/* Events Created */}
                    <Card className="glass-card hover:shadow-xl transition-shadow border-2 border-contribo-navy">
                      <CardContent className="pt-4 px-4 pb-4">
                        <div className="text-center">
                          <div className="bg-contribo-navy bg-opacity-10 p-3 rounded-full w-fit mx-auto mb-2">
                            <PartyPopper className="h-6 w-6 text-contribo-navy" />
                          </div>
                          <p className="text-xs text-gray-600 mb-1">Events Created</p>
                          <p className="text-3xl font-bold text-contribo-navy">
                            {stats.total_events_created}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Events Joined */}
                    <Card className="glass-card hover:shadow-xl transition-shadow border-2 border-purple-600">
                      <CardContent className="pt-4 px-4 pb-4">
                        <div className="text-center">
                          <div className="bg-purple-100 p-3 rounded-full w-fit mx-auto mb-2">
                            <Users className="h-6 w-6 text-purple-600" />
                          </div>
                          <p className="text-xs text-gray-600 mb-1">Events Joined</p>
                          <p className="text-3xl font-bold text-purple-600">
                            {stats.total_events_joined || 0}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Total Contributed */}
                    <Card className="glass-card hover:shadow-xl transition-shadow border-2 border-contribo-gold">
                      <CardContent className="pt-4 px-4 pb-4">
                        <div className="text-center">
                          <div className="bg-contribo-gold bg-opacity-10 p-3 rounded-full w-fit mx-auto mb-2">
                            <HandHeart className="h-6 w-6 text-contribo-gold" />
                          </div>
                          <p className="text-xs text-gray-600 mb-1">Total Contributed</p>
                          <p className="text-2xl font-bold text-contribo-gold">
                            ₹{stats.total_amount_contributed.toLocaleString()}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Total Received */}
                    <Card className="glass-card hover:shadow-xl transition-shadow border-2 border-green-600">
                      <CardContent className="pt-4 px-4 pb-4">
                        <div className="text-center">
                          <div className="bg-green-100 p-3 rounded-full w-fit mx-auto mb-2">
                            <IndianRupee className="h-6 w-6 text-green-600" />
                          </div>
                          <p className="text-xs text-gray-600 mb-1">Total Received</p>
                          <p className="text-2xl font-bold text-green-600">
                            ₹{stats.total_contribution_received.toLocaleString()}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="space-y-4">
                  <p className="text-gray-600 text-lg">Please login to access all features</p>
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-contribo-navy to-contribo-gold text-white px-10 py-6 text-lg rounded-full"
                    onClick={() => setShowAuthModal(true)}
                    data-testid="login-hero-btn"
                  >
                    Login / Sign Up
                  </Button>
                </div>

                {/* Public App Statistics */}
                {publicStats && (
                  <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                    {/* Total Events Created */}
                    <Card className="glass-card hover:shadow-xl transition-shadow border-2 border-contribo-navy">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Events Created</p>
                            <p className="text-4xl font-bold text-contribo-navy">
                              {publicStats.total_events_created}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Across all users</p>
                          </div>
                          <div className="bg-contribo-navy bg-opacity-10 p-4 rounded-full">
                            <PartyPopper className="h-10 w-10 text-contribo-navy" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Total Amount Received */}
                    <Card className="glass-card hover:shadow-xl transition-shadow border-2 border-green-600">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Amount Received</p>
                            <p className="text-4xl font-bold text-green-600">
                              ₹{publicStats.total_amount_received.toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Total contributions</p>
                          </div>
                          <div className="bg-green-100 p-4 rounded-full">
                            <IndianRupee className="h-10 w-10 text-green-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Section - Only for logged-in users */}
      {user && (
        <section className="relative py-16 bg-gradient-to-br from-contribo-navy/5 to-contribo-gold/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-lg text-gray-600">One-time fee based on your event budget. No hidden charges!</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-8">
              <Card className="hover:shadow-xl transition-shadow border-2">
                <CardContent className="pt-4 md:pt-6 px-3 md:px-6 text-center">
                  <h3 className="text-base md:text-xl font-bold text-contribo-navy mb-1 md:mb-2">Basic</h3>
                  <div className="my-2 md:my-4">
                    <span className="text-2xl md:text-4xl font-bold text-contribo-gold">₹49</span>
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">Budget: ₹20k</p>
                  <p className="text-xs md:text-sm font-semibold text-blue-600 mb-2 md:mb-4">25 RSVPs</p>
                  <Button onClick={handleCreateEvent} className="w-full bg-contribo-navy text-xs md:text-sm py-2">
                    Select
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow border-2 border-contribo-navy">
                <CardContent className="pt-4 md:pt-6 px-3 md:px-6 text-center">
                  <div className="bg-contribo-navy text-white px-2 py-0.5 rounded-full text-xs font-semibold mb-1 md:mb-2 inline-block">
                    TOP
                  </div>
                  <h3 className="text-base md:text-xl font-bold text-contribo-navy mb-1 md:mb-2">Standard</h3>
                  <div className="my-2 md:my-4">
                    <span className="text-2xl md:text-4xl font-bold text-contribo-gold">₹99</span>
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">Budget: ₹1L</p>
                  <p className="text-xs md:text-sm font-semibold text-blue-600 mb-2 md:mb-4">60 RSVPs</p>
                  <Button onClick={handleCreateEvent} className="w-full bg-contribo-navy text-xs md:text-sm py-2">
                    Select
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow border-2">
                <CardContent className="pt-4 md:pt-6 px-3 md:px-6 text-center">
                  <h3 className="text-base md:text-xl font-bold text-contribo-navy mb-1 md:mb-2">Premium</h3>
                  <div className="my-2 md:my-4">
                    <span className="text-2xl md:text-4xl font-bold text-contribo-gold">₹199</span>
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">Budget: ₹5L</p>
                  <p className="text-xs md:text-sm font-semibold text-blue-600 mb-2 md:mb-4">200 RSVPs</p>
                  <Button onClick={handleCreateEvent} className="w-full bg-contribo-navy text-xs md:text-sm py-2">
                    Select
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow border-2">
                <CardContent className="pt-4 md:pt-6 px-3 md:px-6 text-center">
                  <h3 className="text-base md:text-xl font-bold text-contribo-navy mb-1 md:mb-2">Enterprise</h3>
                  <div className="my-2 md:my-4">
                    <span className="text-2xl md:text-4xl font-bold text-contribo-gold">₹399</span>
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">Budget: ₹10L</p>
                  <p className="text-xs md:text-sm font-semibold text-blue-600 mb-2 md:mb-4">500 RSVPs</p>
                  <Button onClick={handleCreateEvent} className="w-full bg-contribo-navy text-xs md:text-sm py-2">
                    Select
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="relative py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-4 md:gap-8">
            <div className="glass-card event-card p-4 md:p-8 rounded-2xl">
              <div className="bg-contribo-navy bg-opacity-10 rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mb-3 md:mb-4">
                <Calendar className="h-6 w-6 md:h-8 md:w-8 text-contribo-navy" />
              </div>
              <h3 className="text-base md:text-xl font-semibold mb-2 md:mb-3 text-contribo-navy">Create Your Event</h3>
              <p className="text-sm md:text-base text-gray-600">
                Set up your event with specific contribution goals. Let guests know exactly what you need.
              </p>
            </div>
            <div className="glass-card event-card p-4 md:p-8 rounded-2xl">
              <div className="bg-contribo-gold bg-opacity-20 rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mb-3 md:mb-4">
                <Users className="h-6 w-6 md:h-8 md:w-8 text-contribo-gold-dark" />
              </div>
              <h3 className="text-base md:text-xl font-semibold mb-2 md:mb-3 text-contribo-navy">Guests Contribute</h3>
              <p className="text-sm md:text-base text-gray-600">
                Share your event code. Guests RSVP and contribute to goals they care about.
              </p>
            </div>
            <div className="glass-card event-card p-4 md:p-8 rounded-2xl">
              <div className="bg-contribo-navy-light bg-opacity-10 rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mb-3 md:mb-4">
                <TrendingUp className="h-6 w-6 md:h-8 md:w-8 text-contribo-navy-light" />
              </div>
              <h3 className="text-base md:text-xl font-semibold mb-2 md:mb-3 text-contribo-navy">Track Progress</h3>
              <p className="text-sm md:text-base text-gray-600">
                Monitor RSVPs and contributions in real-time. Know exactly where you stand.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                For Guests
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-contribo-navy bg-opacity-10 flex items-center justify-center mt-1">
                    <span className="text-contribo-navy font-bold">✓</span>
                  </div>
                  <p className="ml-3 text-gray-600">
                    <strong className="text-contribo-navy">Know exactly</strong> how your contribution will be used
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-contribo-navy bg-opacity-10 flex items-center justify-center mt-1">
                    <span className="text-contribo-navy font-bold">✓</span>
                  </div>
                  <p className="ml-3 text-gray-600">
                    <strong className="text-contribo-navy">Skip the stress</strong> of selecting the perfect gift
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-contribo-navy bg-opacity-10 flex items-center justify-center mt-1">
                    <span className="text-contribo-navy font-bold">✓</span>
                  </div>
                  <p className="ml-3 text-gray-600">
                    <strong className="text-contribo-navy">RSVP and contribute</strong> with ease in one place
                  </p>
                </li>
              </ul>
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                For Hosts
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-contribo-gold bg-opacity-20 flex items-center justify-center mt-1">
                    <span className="text-contribo-gold-dark font-bold">✓</span>
                  </div>
                  <p className="ml-3 text-gray-600">
                    <strong className="text-contribo-navy">Set clear goals</strong> for what you truly need
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-contribo-gold bg-opacity-20 flex items-center justify-center mt-1">
                    <span className="text-contribo-gold-dark font-bold">✓</span>
                  </div>
                  <p className="ml-3 text-gray-600">
                    <strong className="text-contribo-navy">Track RSVPs</strong> and guest engagement in real-time
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-contribo-gold bg-opacity-20 flex items-center justify-center mt-1">
                    <span className="text-contribo-gold-dark font-bold">✓</span>
                  </div>
                  <p className="ml-3 text-gray-600">
                    <strong className="text-contribo-navy">Receive funds transparently</strong> for what you actually want
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Modals */}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
        />
      )}
      {showJoinModal && (
        <JoinEventModal onClose={() => setShowJoinModal(false)} />
      )}
    </div>
  );
};

export default HomePage;
